import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Clapperboard, Play } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { FadeUp, SectionTag } from "../components/Motion";
import { IMAGES } from "../data/content";

const SHOWREEL = [
  { id: "21sOenRGl0U", client: "Shree Maahika Rice", film: "TVC Series — Rajatava Dutta" },
  { id: "2vXCcATp29Y", client: "Adya Dairy", film: "Product Film 2024" },
  { id: "rqNA3RX_hK0", client: "KIQ Beer", film: "Inside the Brewery" },
  { id: "bdqaqiX8FFk", client: "Mohor Kutir Resort", film: "Resort Film" },
  { id: "Ak-10eIi_fk", client: "Bhawani Group", film: "Brand Film" },
  { id: "0dlGmaXLGmg", client: "FIFA U-17 World Cup 2017", film: "Campaign Film" },
];

const Studio = () => {
  const [active, setActive] = useState(0);
  return (
  <>
    <PageHeader
      tag="Studio — Podcasts & Shoots"
      title={
        <>
          The floor where brands <span className="text-nexa-red">find their voice.</span>
        </>
      }
      sub="Nexa Studio runs its own production floor in Kolkata — podcast room, shoot floor, edit suite. Concept to final cut without leaving the building."
    />

    <section className="border-b border-white/10 bg-nexa-surface/40" data-testid="showreel-section">
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10">
        <SectionTag>The showreel</SectionTag>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display text-4xl uppercase leading-none text-nexa-bone md:text-6xl">
            Sixty seconds
            <br />
            of <span className="text-nexa-red">proof.</span>
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-nexa-bone/50">
            Six films from the 12 year ledger. Press play — the shoot capability argues for itself.
          </p>
        </div>
        <FadeUp className="mt-10">
          <div className="border border-white/15 shadow-[10px_10px_0_0_rgba(238,33,38,0.35)]" data-testid="showreel-player">
            <div className="aspect-video w-full">
              <iframe
                key={SHOWREEL[active].id}
                src={`https://www.youtube-nocookie.com/embed/${SHOWREEL[active].id}`}
                title={`${SHOWREEL[active].client} — ${SHOWREEL[active].film}`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </FadeUp>
        <div className="mt-6 border-t border-white/10">
          {SHOWREEL.map((f, i) => (
            <button
              key={f.id}
              onClick={() => setActive(i)}
              data-testid={`showreel-item-${i}`}
              className={`grid w-full grid-cols-12 items-baseline gap-2 border-b border-white/10 py-4 text-left transition-colors duration-200 ${
                active === i ? "text-nexa-red" : "text-nexa-bone/60 hover:text-nexa-bone"
              }`}
            >
              <span className="col-span-1 font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
              <span className="col-span-6 font-display text-xl uppercase tracking-tight md:col-span-5 md:text-2xl">{f.client}</span>
              <span className="col-span-5 font-mono text-[10px] uppercase tracking-[0.2em] md:col-span-6 md:text-right">{f.film}</span>
            </button>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10" data-testid="studio-podcasts">
      <div className="grid items-center gap-12 md:grid-cols-2">
        <FadeUp>
          <div className="relative overflow-hidden border border-white/10">
            <a
              href="https://www.youtube.com/watch?v=W5SpUoeyDMo"
              target="_blank"
              rel="noreferrer"
              data-testid="podcast-video-link"
              className="group block"
              aria-label="Watch the Nexa podcast on YouTube"
            >
              <img src={IMAGES.podcast} alt="Nexa podcast" className="h-[420px] w-full object-cover grayscale-[35%] transition-all duration-500 group-hover:grayscale-0" loading="lazy" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-nexa-red text-white opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-105">
                  <Play size={24} fill="currentColor" />
                </span>
              </span>
            </a>
            <span className="pointer-events-none absolute left-4 top-4 bg-nexa-red px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">Podcasts</span>
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <SectionTag>01 — Voice</SectionTag>
          <h2 className="mt-4 font-display text-4xl uppercase leading-none text-nexa-bone md:text-6xl">
            The N<span className="rev-e">E</span>XA Tapes
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-nexa-bone/60">
            Our flagship Nexa Original: long form conversations with founders, marketers and makers on what it
            actually takes to build a brand in India. Plus branded podcasts for clients — concept, recording,
            production, distribution and a clips engine that feeds social for weeks.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-nexa-bone/70">
            {["Show format & identity design", "In studio recording with multi cam video", "Edit, mix & master", "Distribution + 30 short clips per episode"].map((p) => (
              <li key={p} className="flex items-baseline gap-3">
                <Mic size={14} className="shrink-0 text-nexa-red" />
                {p}
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>

    <section className="border-t border-white/10 bg-nexa-surface/40" data-testid="studio-shoots">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 py-24 md:grid-cols-2 md:px-10">
        <FadeUp className="order-2 md:order-1">
          <SectionTag>02 — Frame</SectionTag>
          <h2 className="mt-4 font-display text-4xl uppercase leading-none text-nexa-bone md:text-6xl">
            Shoots that sell, not just shine
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-nexa-bone/60">
            TVCs, product films, corporate films and photography — shot by a crew that sits next to the strategists
            who wrote the brief. Nothing lost in translation between the idea and the frame.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-nexa-bone/70">
            {["TVC & ad film production", "Corporate & brand films", "Product, food & lifestyle photography", "BTS & documentary coverage"].map((p) => (
              <li key={p} className="flex items-baseline gap-3">
                <Clapperboard size={14} className="shrink-0 text-nexa-red" />
                {p}
              </li>
            ))}
          </ul>
          <Link
            to="/contact?service=shoots"
            data-testid="studio-cta-shoots"
            className="group mt-8 inline-flex items-center gap-3 bg-nexa-red px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-nexa-bone hover:text-nexa-black"
          >
            Book the floor <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <FadeUp delay={0.15} className="order-1 md:order-2">
          <div className="relative overflow-hidden border border-white/10">
            <img src={IMAGES.shoot} alt="Nexa shoot floor" className="h-[420px] w-full object-cover grayscale-[35%]" loading="lazy" />
            <span className="absolute left-4 top-4 bg-nexa-bone px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-black">Shoots</span>
          </div>
        </FadeUp>
      </div>
    </section>
  </>
  );
};

export default Studio;
